import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowRight, MoreHorizontal, Play, Plus } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useRecipeFavourites } from "@/hooks/recipe-favourites";
import { usePopularRecipes } from "@/hooks/recipe-views";
import { useRecipes } from "@/hooks/recipes";
import { useAuth } from "@/hooks/useAuth";
import { RecipeSection } from "./recipe-showcase";
import { fromFavourite, fromPopular } from "./recipe-adapters";

export function ChefExperienceWidget() {
  const { i18n } = useLingui();
  const { hasPermission } = useAuth();
  const now = new Date();
  const dayOfWeek = format(now, "EEE");
  const month = format(now, "MMMM");
  const dayNum = format(now, "d");
  const canReadRecipes = hasPermission("recipe:read");

  const { data: favouritesData, isLoading: isFavouritesLoading } = useRecipeFavourites({ limit: 6 }, canReadRecipes);
  const { data: popularData, isLoading: isPopularLoading } = usePopularRecipes({ limit: 6 }, canReadRecipes);
  const { data: firstRecipeData } = useRecipes({ limit: 1, isPublished: true }, canReadRecipes);

  const favourites = favouritesData?.data?.map(fromFavourite) ?? [];
  const popular = popularData?.data?.map(fromPopular) ?? [];
  const firstRecipe = firstRecipeData?.data?.[0];

  const featuredRecipe = popular[0] ?? favourites[0] ?? (firstRecipe ? fromPopular({
    ...firstRecipe,
    viewCount: 0,
    prepTimeMinutes: firstRecipe.prepTimeMinutes ?? undefined,
    cookTimeMinutes: firstRecipe.cookTimeMinutes ?? undefined,
  }) : null);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between px-3">
        <div className="flex flex-col gap-2">
          <h1 className="text-5xl font-semibold leading-none text-(--color-text-primary)">{i18n._(msg`Dashboard`)}</h1>
          <p className="text-base leading-snug text-(--color-text-secondary)">
            {i18n._(msg`Your personalized recipe workspace and performance snapshot.`)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="flex size-13 items-center justify-center overflow-hidden rounded-xl bg-(--shell-muted-bg) shadow-(--shell-shadow)">
              <span className="text-xl font-medium text-(--color-text-primary)">{dayNum}</span>
            </div>
            <p className="w-20 text-base font-medium leading-snug text-(--color-text-secondary)">
              {dayOfWeek}, {month}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="flex h-13 w-30 items-center justify-center gap-2 rounded-xl border border-(--shell-border) bg-(--color-surface-content) shadow-(--shell-shadow) hover:bg-(--shell-muted-bg)"
            >
              <span className="text-base font-medium text-(--color-text-primary)">{i18n._(msg`Export`)}</span>
              <MoreHorizontal className="size-5 text-(--color-text-primary)" />
            </button>
            <button
              type="button"
              className="flex h-13 w-30 items-center justify-center gap-2 rounded-xl bg-(--color-action) hover:bg-(--color-action)/90"
            >
              <Plus className="size-6 text-white" />
              <span className="text-base font-medium text-white">{i18n._(msg`Create`)}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="relative flex h-55 shrink-0 w-full overflow-hidden rounded-2xl bg-(--color-action)">
        {featuredRecipe?.imageUrl ? (
          <img
            src={featuredRecipe.imageUrl}
            alt={featuredRecipe.name}
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-(image:--color-surface-chef-experience)" />
        )}

        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 100% 50%, rgba(19,19,20,0.05) 0%, rgba(19,19,20,0.85) 65%)",
          }}
        />

        <div className="relative flex flex-1 items-start justify-between p-5">
          <div className="flex flex-1 flex-col justify-end gap-3 self-stretch p-2">
            <div className="inline-flex w-fit items-center justify-center rounded-lg bg-white/20 px-4 py-2.5 backdrop-blur-sm">
              <span className="text-base font-medium text-white">{i18n._(msg`Tonight's featured dish`)}</span>
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="text-3xl font-semibold leading-none text-white">
                {featuredRecipe?.name ?? i18n._(msg`Tonight's Special`)}
              </h2>
              <p className="line-clamp-2 max-w-150 text-base leading-snug text-white/70">
                {i18n._(
                  msg`Crispy, savory, and sweet. A comfort classic with bold flavor and balanced textures.`,
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {featuredRecipe ? (
              <Link
                to="/recipes/$recipeId"
                params={{ recipeId: String(featuredRecipe.id) }}
                className="flex h-14 items-center gap-2 rounded-xl bg-(--color-surface-content) px-4 hover:bg-(--color-surface-muted)"
              >
                <span className="text-base font-medium text-(--color-text-primary)">{i18n._(msg`Get the recipe`)}</span>
                <ArrowRight className="size-6 text-(--color-text-primary)" />
              </Link>
            ) : (
              <button
                type="button"
                disabled
                className="flex h-14 items-center gap-2 rounded-xl bg-white/50 px-4 cursor-not-allowed"
              >
                <span className="text-base font-medium text-(--color-text-primary)/50">{i18n._(msg`Get the recipe`)}</span>
                <ArrowRight className="size-6 text-(--color-text-primary)/50" />
              </button>
            )}
            <button
              type="button"
              aria-label={i18n._(msg`Play video`)}
              className="flex items-center justify-center rounded-xl bg-white/20 p-4 backdrop-blur-sm hover:bg-white/30"
            >
              <Play className="size-6 text-white" />
            </button>
          </div>
        </div>
      </div>

      <RecipeSection
        title={i18n._(msg`Favourites`)}
        count={favouritesData?.meta.total ?? 0}
        recipes={favourites}
        isLoading={isFavouritesLoading}
      />

      <RecipeSection
        title={i18n._(msg`Popular`)}
        count={popularData?.meta.total ?? 0}
        recipes={popular}
        isLoading={isPopularLoading}
      />
    </div>
  );
}
